/**
 * Breathing Cue Choice Component - Chapter 1 Only
 * 
 * Trauma-informed breathing exercise that offers two approaches:
 * 1. Follow the Pattern (guided breathing with counts)
 * 2. Notice Your Breath (gentle observation without control)
 * 
 * Based on mental health professional feedback that directed breathing
 * can be distressing for trauma-sensitized or dysregulated children.
 */

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { X, Wind, Eye, Info, Leaf } from "lucide-react";

interface BreathingCueChoiceProps {
  onComplete: () => void;
  isCompleted: boolean;
  trackButton?: (buttonId: string, metadata?: any) => void;
}

/**
 * Breathing Mode Component
 * Shows either guided breathing or gentle observation prompts
 */
function BreathingMode({ mode, onBack }: { mode: 'guided' | 'observe'; onBack: () => void }) {
  const [currentPrompt, setCurrentPrompt] = useState(0);
  
  const guidedSteps = [
    "Sit comfortably and close your eyes (or soften your gaze)",
    "Breathe in slowly through your nose... count to 4",
    "Hold your breath gently... count to 2",
    "Breathe out slowly through your mouth... count to 6",
    "Feel your body relax like ripples on a pond"
  ];
  
  const observePrompts = [
    "Where do you feel your breath? Your nose? Your Chest? Your belly?",
    "Is the air cool when it comes in? Warm when it goes out?",
    "Does your belly move when you breathe? Or your chest?",
    "Just notice... no need to change anything.",
    "You're doing great just by paying attention."
  ];
  
  const steps = mode === 'guided' ? guidedSteps : observePrompts;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Mode Title */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-lg font-semibold text-forest-800">
          {mode === 'guided' ? (
            <><Wind className="w-5 h-5 text-blue-500" /> Follow the Pattern</>
          ) : (
            <><Eye className="w-5 h-5 text-green-500" /> Notice Your Breath</>
          )}
        </div>
      </div>
      
      {/* Prompt Display with gentle animation */}
      <div className="bg-cream-50 rounded-lg p-6 min-h-[120px] flex items-center justify-center border-2 border-golden-200">
        <AnimatePresence mode="wait">
          <motion.p
            key={currentPrompt}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-xl text-gray-800 text-center leading-relaxed"
          >
            {steps[currentPrompt]}
          </motion.p>
        </AnimatePresence>
      </div>
      
      {/* Visual Indicator */}
      {mode === 'guided' && (
        <div className="flex justify-center">
          <motion.div
            className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-blue-600"
            animate={{
              scale: [1, 1.3, 1, 0.9, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              times: [0, 0.33, 0.5, 0.83, 1]
            }}
          />
        </div>
      )}
      
      {mode === 'observe' && (
        <div className="flex justify-center">
          <motion.div
            className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-green-600 opacity-60"
            animate={{
              opacity: [0.6, 0.8, 0.6]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>
      )}
      
      {/* Progress Dots */}
      <div className="flex justify-center gap-2">
        {steps.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentPrompt(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentPrompt
                ? 'bg-forest-600 w-6'
                : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between items-center pt-2">
        <Button
          variant="ghost"
          onClick={onBack}
          className="text-gray-600 hover:text-gray-800"
        >
          ← Back to choices
        </Button>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPrompt((p) => Math.max(0, p - 1))}
            disabled={currentPrompt === 0}
            className="text-sm"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setCurrentPrompt((p) => Math.min(steps.length - 1, p + 1))}
            disabled={currentPrompt === steps.length - 1}
            className="text-sm"
          >
            Next
          </Button>
        </div>
      </div>
      
      {/* Done Button */}
      {currentPrompt === steps.length - 1 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center pt-4"
        >
          <Button
            onClick={() => {
              onBack();
              // Add small delay to allow animation, then trigger completion
              setTimeout(() => {
                if (window.location.pathname.includes('chapter')) {
                  // Trigger the handleComplete from parent component
                  const completeEvent = new CustomEvent('breathing-complete');
                  window.dispatchEvent(completeEvent);
                }
              }, 300);
            }}
            className="bg-forest-600 hover:bg-forest-700 text-white px-8 py-3 text-base font-medium"
          >
            {mode === 'guided' ? 'I took a pond breath!' : 'I noticed my breath!'}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}

/**
 * Parent/Educator Info Panel
 * Shows trauma-informed guidance for adults
 */
function EducatorInfo({ onClose }: { onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 space-y-4"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">For Parents & Educators</h4>
        </div>
        <button
          onClick={onClose}
          className="text-blue-400 hover:text-blue-600"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      <div className="text-sm text-blue-800 space-y-3">
        <p>
          <strong>Why two breathing approaches?</strong>
        </p>
        <p>
          For trauma-sensitized children or those who are dysregulated, 
          directed breathing exercises can sometimes increase distress rather than calm.
        </p>
        <p>
          <strong>"Notice Your Breath"</strong> offers a gentle alternative where children 
          simply observe their breath without trying to control it. This mindful observation 
          is equally effective for developing self-awareness.
        </p>
        <p>
          Let the child choose the approach that feels comfortable. Both are valid paths 
          to mindfulness.
        </p>
      </div>
    </motion.div>
  );
}

/**
 * Main Breathing Cue Choice Component
 * Entry point that shows two breathing paths
 */
export function BreathingCueChoice({
  onComplete,
  isCompleted,
  trackButton
}: BreathingCueChoiceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'guided' | 'observe' | null>(null);
  const [showEducatorInfo, setShowEducatorInfo] = useState(false);

  const handleClose = () => {
    setSelectedMode(null);
    setShowEducatorInfo(false);
    setIsOpen(false);
  };

  return (
    <>
      {/* Glowing button to trigger the choice modal */}
      <motion.button
        className="relative bg-golden-100 p-8 rounded-full border-4 border-white/70 shadow-2xl backdrop-blur-sm w-24 h-24 flex items-center justify-center"
        onClick={() => {
          trackButton?.('breathing_cue_open', { cue_name: 'Golden Leaf' });
          setIsOpen(true);
        }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          scale: [1, 1.25, 1],
          boxShadow: [
            "0 0 0 0 rgba(251, 191, 36, 0.7)",
            "0 0 0 30px rgba(251, 191, 36, 0)",
            "0 0 0 0 rgba(251, 191, 36, 0.7)",
          ],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        disabled={isCompleted}
      >
        <Leaf className="w-12 h-12 text-golden-600" />
        {isCompleted && (
          <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold">
            ✓
          </div>
        )}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={handleClose}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-200 relative">
                {/* Cue Icon - Left Corner */}
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <div className="w-10 h-10 rounded-full bg-golden-50 text-golden-600 border-2 border-current flex items-center justify-center">
                    <Leaf className="w-5 h-5" />
                  </div>
                </div>
                
                {/* Close Button - Right Corner */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
                
                {/* Centered Title */}
                <div className="text-center px-16">
                  <h3 className="text-xl font-bold">
                    <span className="font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 bg-clip-text text-transparent">
                      Pond Breaths
                    </span>
                  </h3>
                </div>
              </div>
            
              {/* Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {/* Educator Info Panel */}
                  {showEducatorInfo && (
                    <EducatorInfo onClose={() => setShowEducatorInfo(false)} />
                  )}
                  
                  {/* Choice Screen */}
                  {!selectedMode && !showEducatorInfo && (
                    <motion.div
                      key="choice"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-4"
                    >
                      <p className="text-center text-gray-700 mb-2">
                        Choose the breathing path that is best in this moment:
                      </p>
                      
                      {/* Parent/Educator Info Link - Right under intro text */}
                      <button
                        onClick={() => setShowEducatorInfo(!showEducatorInfo)}
                        className="flex items-center gap-1 mx-auto text-sm text-blue-600 hover:text-blue-800 mb-4"
                      >
                        <Info className="w-4 h-4" />
                        For Parents/Educators
                      </button>
                      
                      {/* Guided Breathing Option */}
                      <motion.button
                        onClick={() => {
                          trackButton?.('breathing_path_guided', { cue_name: 'Golden Leaf' });
                          setSelectedMode('guided');
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl text-left hover:border-blue-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                            <Wind className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-900 mb-1">Follow the Pattern</h4>
                            <p className="text-sm text-blue-700">
                              Breathe in, hold, breathe out—follow Wise Frog's gentle rhythm
                            </p>
                          </div>
                        </div>
                      </motion.button>
                      
                      {/* Observe Breathing Option */}
                      <motion.button
                        onClick={() => {
                          trackButton?.('breathing_path_observe', { cue_name: 'Golden Leaf' });
                          setSelectedMode('observe');
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl text-left hover:border-green-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                            <Eye className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-green-900 mb-1">Notice Your Breath</h4>
                            <p className="text-sm text-green-700">
                              Simply observe your breath—where it is, how it feels, no changes needed
                            </p>
                          </div>
                        </div>
                      </motion.button>
                      
                      <p className="text-center text-sm text-gray-500 mt-4">
                        Both ways are wonderful! Choose what feels right for you today.
                      </p>
                    </motion.div>
                  )}
                  
                  {/* Breathing Mode Screen */}
                  {selectedMode && !showEducatorInfo && (
                    <BreathingMode
                      key={selectedMode}
                      mode={selectedMode}
                      onBack={() => setSelectedMode(null)}
                    />
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
